// src/user/contexts/WorkflowCacheContext.jsx

import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import ApiClient from '../../shared/services/ApiClient';
import { fetchWithAuth } from '../../admin/auth/fetchWithAuth';
import { fetchWorkflowDetails } from '../services/api/workflows';
import { fetchTaskWorkflow } from '../services/api/tasks';
import {
  saveWorkflowSnapshot,
  getWorkflowSnapshot,
  extractFormRefs,
  saveFormDef,
  getFormDef,
  saveDraft as storageSaveDraft,
  getDraft as storageGetDraft,
  getPhotoDraft as storageGetPhotoDraft,
  savePhotoDraft as storageSavePhotoDraft,
  PHOTO_CAPTURE_DRAFT_SLUG,
  iterateDrafts,
  clearTaskNamespace as storageClearTaskNamespace,
  sweepOrphanedTaskNamespaces,
  getTrajectory as storageGetTrajectory,
  saveTrajectory as storageSaveTrajectory,
  createEmptyTrajectory,
  recordStepVisit as storageRecordStepVisit,
  recordTransition as storageRecordTransition,
} from '../utils/taskStorage';
import { API_URL } from '../../config/env';

/**
 * Centralized, task-scoped cache for:
 *  - workflow snapshot (at task open)
 *  - form definitions (loaded once per task)
 *  - draft answers (local writes on change)
 *
 * NO cross-task reuse. NO background refresh.
 */

const WorkflowCacheContext = createContext(null);

export function WorkflowCacheProvider({ children }) {
  // Optional in-memory read-through caches for this session
  const defsCacheRef = useRef(new Map());   // key => formDef
  const draftsCacheRef = useRef(new Map()); // key => answers
  const trajectoryCacheRef = useRef(new Map()); // key => trajectory

  useEffect(() => {
    sweepOrphanedTaskNamespaces();
  }, []);

  const api = useMemo(() => {
    // Build a stable API object
    return {
      ensureTaskSnapshot,
      ensureWorkflowDefinition,
      ensureAllFormDefinitions,
      checkAllFormsPresent,
      getFormDefinition,
      getDraft,
      setDraft,
      getPhotoDraft,
      setPhotoDraft,
      recordStepVisit,
      recordTransition,
      assembleWorkReports,
      clearTaskNamespace,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable context API object for provider lifetime
  }, []);

  // ------- API methods -------

  function ensureTaskSnapshot({ accessKey, taskId, workflow, force = false }) {
    const existing = getWorkflowSnapshot(accessKey, taskId);
    if (!existing || force) {
      saveWorkflowSnapshot(accessKey, taskId, workflow);
    }
    return getWorkflowSnapshot(accessKey, taskId);
  }

  async function ensureWorkflowDefinition({ accessKey, taskId, workflowId, signal } = {}) {
    // Check if we already have this workflow in the snapshot
    const snapshot = getWorkflowSnapshot(accessKey, taskId);
    if (snapshot) {
      return { ok: true, workflow: snapshot };
    }

    try {
      let workflow;
      if (accessKey === "preview") {
        // Admin preview mode - direct workflow access
        workflow = await fetchWorkflowDetails(workflowId, { signal });
      } else {
        // User mode - workflow tied to specific task
        workflow = await fetchTaskWorkflow(accessKey, taskId, { signal });
      }
      
      // Save as workflow snapshot for this task
      saveWorkflowSnapshot(accessKey, taskId, workflow);
      return { ok: true, workflow };
    } catch (error) {
      return { ok: false, error };
    }
  }

  async function ensureAllFormDefinitions({ accessKey, taskId, signal } = {}) {
    const snapshot = getWorkflowSnapshot(accessKey, taskId);
    if (!snapshot) {
      return { ok: false, error: new Error('No workflow snapshot found') };
    }
    const formRefs = extractFormRefs(snapshot);
    const missing = formRefs.filter(
      ({ stepId, slug }) => !getFormDef(accessKey, taskId, stepId, slug)
    );

    if (missing.length === 0) return { ok: true };

    const results = await Promise.allSettled(
      missing.map(async ({ stepId, slug }) => {
        let def;
        
        // Use admin API for preview mode, user API for real tasks
        if (accessKey === "preview") {
          // Admin preview mode - use admin forms API
          try {
            const response = await fetchWithAuth(`${API_URL}/v1/admin/forms?slug=${encodeURIComponent(slug)}`);
            if (!response.ok) {
              throw new Error(`Failed to fetch form: ${response.status}`);
            }
            const forms = await response.json();
            const form = forms.find(f => f.slug === slug);
            if (!form) {
              throw new Error(`Form not found: ${slug}`);
            }
            
            // Get full form definition by ID
            const fullResponse = await fetchWithAuth(`${API_URL}/v1/admin/forms/${form.id}`);
            if (!fullResponse.ok) {
              throw new Error(`Failed to fetch form details: ${fullResponse.status}`);
            }
            const fullForm = await fullResponse.json();
            
            // Transform to match user API format
            def = {
              slug: fullForm.slug,
              title: fullForm.title,
              description: fullForm.description,
              schema: fullForm.schema,
              uiSchema: fullForm.uiSchema,
              defaults: fullForm.defaults,
              meta: fullForm.meta
            };
          } catch (error) {
            console.error('Error fetching form from admin API:', error);
            throw error;
          }
        } else {
          // User mode - use existing ApiClient
          def = await ApiClient.getForm(accessKey, slug, { signal });
        }
        
        // Persist to storage and in-memory cache
        saveFormDef(accessKey, taskId, stepId, slug, def);
        defsCacheRef.current.set(formDefCacheKey(accessKey, taskId, stepId, slug), def);
        return { stepId, slug };
      })
    );

    const rejected = results.filter((r) => r.status === 'rejected');
    if (rejected.length > 0) {
      const error = new Error('Failed to load some form definitions');
      return {
        ok: false,
        error,
        details: rejected.map((r, i) => ({
          ref: missing[i],
          reason: r.reason?.message || String(r.reason),
        })),
      };
    }

    return { ok: true };
  }

  function checkAllFormsPresent({ accessKey, taskId }) {
    const snapshot = getWorkflowSnapshot(accessKey, taskId);
    if (!snapshot) return false;
    const formRefs = extractFormRefs(snapshot);
    if (formRefs.length === 0) return true;
    return formRefs.every(({ stepId, slug }) => !!getFormDef(accessKey, taskId, stepId, slug));
  }

  function getFormDefinition({ accessKey, taskId, stepId, slug }) {
    const k = formDefCacheKey(accessKey, taskId, stepId, slug);
    if (defsCacheRef.current.has(k)) return defsCacheRef.current.get(k);

    const def = getFormDef(accessKey, taskId, stepId, slug);
    if (def) defsCacheRef.current.set(k, def);
    return def || null;
  }

  function getDraft({ accessKey, taskId, stepId, slug }) {
    const k = draftCacheKey(accessKey, taskId, stepId, slug);
    if (draftsCacheRef.current.has(k)) return draftsCacheRef.current.get(k);

    const draft = storageGetDraft(accessKey, taskId, stepId, slug);
    if (draft) draftsCacheRef.current.set(k, draft);
    return draft || null;
  }

  function setDraft({ accessKey, taskId, stepId, slug, answers }) {
    const k = draftCacheKey(accessKey, taskId, stepId, slug);
    draftsCacheRef.current.set(k, answers || {});
    storageSaveDraft(accessKey, taskId, stepId, slug, answers || {});
  }

  function getPhotoDraft({ accessKey, taskId, stepId }) {
    const k = draftCacheKey(accessKey, taskId, stepId, PHOTO_CAPTURE_DRAFT_SLUG);
    if (draftsCacheRef.current.has(k)) return draftsCacheRef.current.get(k);

    const draft = storageGetPhotoDraft(accessKey, taskId, stepId);
    if (draft) draftsCacheRef.current.set(k, draft);
    return draft || null;
  }

  function setPhotoDraft({ accessKey, taskId, stepId, capture }) {
    const k = draftCacheKey(accessKey, taskId, stepId, PHOTO_CAPTURE_DRAFT_SLUG);
    draftsCacheRef.current.set(k, capture || null);
    storageSavePhotoDraft(accessKey, taskId, stepId, capture);
  }

  function trajectoryCacheKey(accessKey, taskId) {
    return `${taskCachePrefix(accessKey, taskId)}trajectory`;
  }

  function getTrajectoryState({ accessKey, taskId }) {
    const k = trajectoryCacheKey(accessKey, taskId);
    if (trajectoryCacheRef.current.has(k)) {
      return trajectoryCacheRef.current.get(k);
    }
    const trajectory = storageGetTrajectory(accessKey, taskId);
    trajectoryCacheRef.current.set(k, trajectory);
    return trajectory;
  }

  function persistTrajectory({ accessKey, taskId, trajectory }) {
    const k = trajectoryCacheKey(accessKey, taskId);
    trajectoryCacheRef.current.set(k, trajectory);
    storageSaveTrajectory(accessKey, taskId, trajectory);
  }

  function recordStepVisit({ accessKey, taskId, stepId }) {
    if (!stepId) return;
    const current = getTrajectoryState({ accessKey, taskId }) || createEmptyTrajectory();
    const next = storageRecordStepVisit(current, stepId);
    persistTrajectory({ accessKey, taskId, trajectory: next });
  }

  function recordTransition({ accessKey, taskId, from, to, actionIndex, actionText }) {
    if (!from || !to) return;
    const current = getTrajectoryState({ accessKey, taskId }) || createEmptyTrajectory();
    const next = storageRecordTransition(current, {
      from,
      to,
      actionIndex,
      actionText,
    });
    persistTrajectory({ accessKey, taskId, trajectory: next });
  }

  function assembleWorkReports({ accessKey, taskId }) {
    const out = {};
    for (const { stepId, slug, answers } of iterateDrafts(accessKey, taskId)) {
      if (!out[stepId]) {
        out[stepId] = {};
      }
      if (slug === PHOTO_CAPTURE_DRAFT_SLUG) {
        out[stepId]._photo_capture = answers || {};
      } else {
        out[stepId][slug] = answers || {};
      }
    }

    const trajectory = getTrajectoryState({ accessKey, taskId });
    const hasVisits = trajectory?.visits && Object.keys(trajectory.visits).length > 0;
    const hasTransitions = trajectory?.transitions?.length > 0;
    if (hasVisits || hasTransitions) {
      out._meta = {
        schema_version: trajectory.schema_version || 1,
        visits: trajectory.visits || {},
        transitions: trajectory.transitions || [],
        transitions_dropped: trajectory.transitions_dropped || 0,
      };
    }

    return { work_reports: out };
  }

  function clearTaskNamespace({ accessKey, taskId }) {
    defsCacheRef.current.forEach((_, k) => {
      if (k.startsWith(taskCachePrefix(accessKey, taskId))) defsCacheRef.current.delete(k);
    });
    draftsCacheRef.current.forEach((_, k) => {
      if (k.startsWith(taskCachePrefix(accessKey, taskId))) draftsCacheRef.current.delete(k);
    });
    trajectoryCacheRef.current.forEach((_, k) => {
      if (k.startsWith(taskCachePrefix(accessKey, taskId))) trajectoryCacheRef.current.delete(k);
    });
    storageClearTaskNamespace(accessKey, taskId);
  }

  // ------- helpers -------
  function formDefCacheKey(accessKey, taskId, stepId, slug) {
    return `${taskCachePrefix(accessKey, taskId)}form::${stepId}::${slug}`;
  }

  function draftCacheKey(accessKey, taskId, stepId, slug) {
    return `${taskCachePrefix(accessKey, taskId)}draft::${stepId}::${slug}`;
  }

  function taskCachePrefix(accessKey, taskId) {
    return `mem::${accessKey}::${taskId}::`;
  }

  return (
    <WorkflowCacheContext.Provider value={api}>
      {children}
    </WorkflowCacheContext.Provider>
  );
}

export function useWorkflowCache() {
  const ctx = useContext(WorkflowCacheContext);
  if (!ctx) {
    throw new Error('useWorkflowCache must be used within a WorkflowCacheProvider');
  }
  return ctx;
}
